import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Leaf, Inbox, Users, FolderKanban, Receipt, LineChart, Target, ArrowRight, Check,
} from "lucide-react";
import Seo from "@/components/Seo";
import SupportBanner from "@/components/SupportBanner";

const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://freelanzen.it").replace(/\/$/, "");

const LANDING_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "FreelanZen",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web",
  "description": "Piattaforma all-in-one per freelance italiani: CRM, inbox WhatsApp, fatturazione elettronica SdI, solleciti AI, tracker ore e reportistica.",
  "inLanguage": "it",
  "url": `${SITE_URL}/`,
  "offers": [
    {
      "@type": "Offer",
      "name": "Starter (mensile)",
      "price": "9.99",
      "priceCurrency": "EUR",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "9.99",
        "priceCurrency": "EUR",
        "billingIncrement": "P1M"
      }
    },
    {
      "@type": "Offer",
      "name": "Starter (annuale)",
      "price": "99.00",
      "priceCurrency": "EUR",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "99.00",
        "priceCurrency": "EUR",
        "billingIncrement": "P1Y"
      }
    }
  ],
  "publisher": {
    "@type": "Organization",
    "name": "FreelanZen"
  }
};

const Feature = ({ icon: Icon, title, desc }) => (
  <div className="zen-card p-6 md:p-8 animate-fade-up">
    <div className="w-12 h-12 rounded-xl bg-zen-primary/10 text-zen-primary flex items-center justify-center mb-5">
      <Icon size={22} strokeWidth={1.5} />
    </div>
    <h3 className="font-heading text-xl mb-2 text-zen-ink">{title}</h3>
    <p className="text-zen-ink2 leading-relaxed text-[15px]">{desc}</p>
  </div>
);

export default function Landing() {
  const navigate = useNavigate();
  // Capture referral code from URL if present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) localStorage.setItem("fz_ref", ref.toUpperCase());
  }, []);
  return (
    <div className="min-h-screen bg-zen-bg text-zen-ink">
      <Seo
        title="FreelanZen — Il lavoro da freelance, in un unico spazio calmo"
        description="CRM, inbox WhatsApp, fatturazione elettronica SdI e solleciti AI per freelance italiani. Unifica clienti, progetti e fatture in un unico spazio calmo."
        path="/"
        image="https://images.pexels.com/photos/8408550/pexels-photo-8408550.jpeg?auto=compress&cs=tinysrgb&w=1200&h=630&fit=crop"
        imageAlt="Scrivania minimale con bonsai — FreelanZen"
        jsonLd={LANDING_JSON_LD}
      />
      {/* Nav */}
      <header className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
        <img
          src="/brand/freelanzen-wordmark.png"
          alt="FreelanZen"
          className="h-8 md:h-9 w-auto object-contain"
        />
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="nav-login-link">
            <Button variant="ghost" className="text-zen-ink hover:bg-zen-subtle/20 rounded-full">Accedi</Button>
          </Link>
          <Button
            data-testid="nav-cta-btn"
            onClick={() => navigate("/login?mode=register")}
            className="bg-zen-primary hover:bg-zen-primaryHover text-white rounded-full px-5"
          >
            Inizia gratis
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <span className="zen-label">Piattaforma all-in-one per freelance</span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-heading tracking-tight leading-[1.05]">
              Il lavoro del freelance,<br/>
              <span className="text-zen-primary">finalmente in ordine.</span>
            </h1>
            <p className="mt-6 text-lg text-zen-ink2 max-w-xl leading-relaxed">
              FreelanZen unifica email, WhatsApp, clienti, progetti, fatture e follow-up
              in un unico spazio calmo. Meno caos, più tempo per lavorare bene.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                data-testid="hero-cta-btn"
                onClick={() => navigate("/login?mode=register")}
                className="bg-zen-primary hover:bg-zen-primaryHover text-white rounded-full px-7 py-6 text-base"
              >
                Prova gratis <ArrowRight size={18} className="ml-2"/>
              </Button>
              <div className="flex items-center gap-2 text-sm text-zen-ink2">
                <Check size={16} className="text-zen-primary"/> Nessuna carta richiesta
              </div>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-zen-ink2">
              <div><span className="font-heading text-2xl text-zen-ink">3.2h</span><br/>risparmiate/settimana</div>
              <div className="w-px h-10 bg-zen-border"/>
              <div><span className="font-heading text-2xl text-zen-ink">+27%</span><br/>fatture pagate in tempo</div>
              <div className="w-px h-10 bg-zen-border hidden sm:block"/>
              <div className="hidden sm:block"><span className="font-heading text-2xl text-zen-ink">1</span><br/>solo spazio di lavoro</div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="zen-card p-6 relative overflow-hidden">
              <img
                src="https://images.pexels.com/photos/8408550/pexels-photo-8408550.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Scrivania minimale con bonsai"
                className="w-full h-64 object-cover rounded-xl"
              />
              <div className="mt-5 grid grid-cols-3 gap-3">
                <div className="bg-zen-bg rounded-xl p-4">
                  <div className="zen-label">Ricavi</div>
                  <div className="font-heading text-2xl mt-1">€8.4k</div>
                </div>
                <div className="bg-zen-bg rounded-xl p-4">
                  <div className="zen-label">Ore</div>
                  <div className="font-heading text-2xl mt-1">142</div>
                </div>
                <div className="bg-zen-bg rounded-xl p-4">
                  <div className="zen-label">Margine</div>
                  <div className="font-heading text-2xl mt-1">68%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="max-w-2xl mb-12">
          <span className="zen-label">Tutto in un posto</span>
          <h2 className="mt-3 text-3xl md:text-4xl font-heading tracking-tight">
            Molti strumenti, una piattaforma serena.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Feature icon={Inbox} title="Inbox unificata" desc="Email centralizzata e chat WhatsApp a un click dal cliente giusto, senza esporre il tuo numero."/>
          <Feature icon={Users} title="CRM cliente-centrico" desc="Rubrica, storico interazioni e tag, senza fogli di calcolo."/>
          <Feature icon={FolderKanban} title="Progetti & attività" desc="Stato, avanzamento e ore fatturabili sotto controllo."/>
          <Feature icon={Receipt} title="Fatture & follow-up" desc="Solleciti gentili generati dall'AI, pronti da inviare."/>
          <Feature icon={LineChart} title="Report & insight AI" desc="Ricavi, marginalità e suggerimenti operativi per crescere."/>
          <Feature icon={Target} title="Obiettivi" desc="Traguardi mensili e trimestrali monitorati automaticamente."/>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="bg-white border-t border-zen-border">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 text-center">
          <h3 className="font-heading text-3xl md:text-4xl">Ritrova il ritmo giusto.</h3>
          <p className="mt-4 text-zen-ink2 text-lg">Inizia oggi con FreelanZen. Zero carta di credito, prova gratis per 30 giorni.</p>
          <Button
            data-testid="footer-cta-btn"
            onClick={() => navigate("/login?mode=register")}
            className="mt-8 bg-zen-primary hover:bg-zen-primaryHover text-white rounded-full px-8 py-6 text-base"
          >
            Crea il mio spazio
          </Button>
        </div>
        <SupportBanner variant="site" />
        <div className="border-t border-zen-border py-6 text-center text-sm text-zen-ink2 space-y-2">
          <div className="flex justify-center gap-4 flex-wrap">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-zen-primary">Privacy Policy</Link>
            <span aria-hidden>·</span>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-zen-primary">Termini di Servizio</Link>
            <span aria-hidden>·</span>
            <a href="mailto:supporto@freelanzen.it" className="hover:text-zen-primary">Contatti</a>
          </div>
          <div>© 2026 FreelanZen — Il tuo studio, semplificato.</div>
        </div>
      </section>
    </div>
  );
}
